/**
 * PinGuard.jsx — Capa de seguridad UI para salidas de dinero en el modulo Bancos.
 *
 * Uso:
 *   const { requirePin, PinGuardUI } = useRequirePin();
 *   const handleSend = async () => {
 *     if (!(await requirePin())) return;
 *     // ...ejecuta el fetch/payout
 *   };
 *   // Render en algun lado del componente:
 *   return (<>{PinGuardUI}...</>)
 *
 * Detalles:
 *   - Hashea el PIN con SHA-256 antes de comparar, para no exponer el numero
 *     plano en el bundle. El hash es constante y esta duro en el codigo.
 *   - Cachea la validacion en sessionStorage (dura hasta cerrar la pestaña).
 *   - No modifica lógica de backend ni de payouts. Solo bloquea la ejecución
 *     hasta que el usuario ingrese el PIN correcto.
 *   - Escape: click fuera / X / botón Cancelar -> el promise resuelve `false`
 *     y la accion NO se ejecuta.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Lock, X, ShieldAlert } from 'lucide-react';

// SHA-256("3131") — cambiar aca si se rota el PIN.
const PIN_HASH = '656d5be9b84397105deefd04cb2e83f4f3d448bccfaa953d6f62021b50f6488a';
const SESSION_FLAG_KEY = 'bancos_pin_validated_v1';

async function sha256Hex(str) {
  const buf = new TextEncoder().encode(str);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export function useRequirePin() {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const resolverRef = useRef(null);

  const isValidated = useCallback(() => {
    try { return sessionStorage.getItem(SESSION_FLAG_KEY) === '1'; } catch { return false; }
  }, []);

  const closeAndResolve = useCallback((ok) => {
    setOpen(false);
    setPin('');
    setError('');
    setChecking(false);
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(ok);
  }, []);

  const requirePin = useCallback(() => {
    if (isValidated()) return Promise.resolve(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setPin('');
      setError('');
      setOpen(true);
    });
  }, [isValidated]);

  const submit = useCallback(async () => {
    if (!pin.trim()) { setError('Ingresá el PIN'); return; }
    setChecking(true);
    setError('');
    try {
      const hash = await sha256Hex(pin.trim());
      if (hash === PIN_HASH) {
        try { sessionStorage.setItem(SESSION_FLAG_KEY, '1'); } catch { /* storage bloqueado */ }
        closeAndResolve(true);
      } else {
        setError('PIN incorrecto');
        setPin('');
        setChecking(false);
      }
    } catch (e) {
      setError('Error validando PIN');
      setChecking(false);
    }
  }, [pin, closeAndResolve]);

  const PinGuardUI = useMemo(() => {
    if (!open) return null;
    return (
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={() => closeAndResolve(false)}
        data-testid="pin-guard-overlay"
      >
        <div
          className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl w-full max-w-sm"
          onClick={(e) => e.stopPropagation()}
          data-testid="pin-guard-modal"
        >
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 rounded-t-2xl bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/30 dark:to-orange-900/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-100 dark:bg-red-900/50">
                  <ShieldAlert className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-gray-100">Confirmación de salida</h3>
                  <p className="text-xs text-gray-500">Ingresá el PIN para autorizar</p>
                </div>
              </div>
              <button
                onClick={() => closeAndResolve(false)}
                className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                data-testid="pin-guard-close-btn"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="px-6 py-6 space-y-4">
            <div className="flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                <Lock className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 text-center">PIN de seguridad</label>
              <input
                type="password"
                inputMode="numeric"
                autoFocus
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                placeholder="••••"
                className="w-full text-center text-3xl tracking-[0.7em] py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 focus:border-red-500 focus:outline-none font-mono"
                data-testid="pin-guard-input"
                disabled={checking}
              />
            </div>

            {error && (
              <div
                className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300 text-center"
                data-testid="pin-guard-error"
              >
                {error}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => closeAndResolve(false)}
                disabled={checking}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
                data-testid="pin-guard-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={submit}
                disabled={checking || !pin.trim()}
                className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pin-guard-confirm-btn"
              >
                {checking ? 'Verificando...' : 'Autorizar'}
              </button>
            </div>

            <p className="text-[10px] text-gray-400 text-center">
              La autorización queda activa hasta que cierres la pestaña
            </p>
          </div>
        </div>
      </div>
    );
  }, [open, pin, error, checking, submit, closeAndResolve]);

  return { requirePin, PinGuardUI, isValidated };
}
